import db from "../db/database";
import { fetchFromBusinessCentralEspecial } from "../utils/businessCentralUtils";
import { saveSalesPricesToLocalDB } from "./salesPriceService";
import { isOnline } from "./syncService";

// Función para sincronizar precios de venta desde Business Central
export const syncSalesPrices = async () => {
    if (!isOnline()) {
        console.log("Sin conexión a internet, no se puede sincronizar precios de venta");
        return false;
    }

    try {
        console.log("Sincronizando precios de venta desde Business Central...");

        const localSalesPrices = await db.salesPrices.toArray();
        let processedServerPrices = 0;
        let lastSourceCode = null;

        console.log("localSalesPrices", localSalesPrices);

        if (localSalesPrices.length > 0) {
            lastSourceCode = localSalesPrices.sort((a, b) => a.Source_Counter - b.Source_Counter)[localSalesPrices.length - 1].Source_Counter;
        }

        const response = await fetchFromBusinessCentralEspecial("Importaciones_getcurrentsalesprices", {
            data: JSON.stringify({
                Source_Counter: lastSourceCode ? lastSourceCode : 0
            })
        });

        if (!lastSourceCode) {
            await db.salesPrices.clear();

            // Guardar todos los precios del servidor
            await saveSalesPricesToLocalDB(JSON.parse(response).SalesPrices);
        } else {
            // Procesar respuesta del servidor
            if (response) {
                const serverPrices = JSON.parse(response).SalesPrices;
                processedServerPrices = Array.isArray(serverPrices) ? serverPrices.length : 0;

                console.log("parseResponse");

                if (serverPrices && serverPrices.length > 0) {
                    // Crear mapa de precios locales para búsqueda rápida
                    const localPricesByKey = {};
                    localSalesPrices.forEach(price => {
                        const key = `${price.Item_No}-${price.Sales_Type}-${price.Sales_Code}`;
                        localPricesByKey[key] = price;
                    });

                    console.log("localPricesByKey");

                    // Preparar datos para operaciones en bloque
                    const pricesToUpdate = [];
                    const pricesToAdd = [];

                    for (const serverPrice of serverPrices) {
                        const key = `${serverPrice.Item_No}-${serverPrice.Sales_Type}-${serverPrice.Sales_Code}`;
                        const localPrice = localPricesByKey[key];

                        if (localPrice) {
                            // Preparar actualización si no está pendiente de sincronización
                            if (!localPrice.pendingSync) {
                                pricesToUpdate.push({
                                    key: localPrice.id,
                                    changes: {
                                        ...serverPrice,
                                        Source_Counter: serverPrice.Source_Counter,
                                        lastModified: new Date(),
                                        lastSynced: new Date(),
                                        pendingSync: false,
                                        deleted: false
                                    }
                                });
                            }
                        } else {
                            // Preparar inserción de nuevo precio
                            pricesToAdd.push({
                                Item_No: serverPrice.Item_No,
                                Sales_Type: serverPrice.Sales_Type,
                                Sales_Code: serverPrice.Sales_Code,
                                Minimum_Quantity: serverPrice.Minimum_Quantity,
                                Unit_of_Measure_Code: serverPrice.Unit_of_Measure_Code,
                                Starting_Date: serverPrice.Starting_Date,
                                Ending_Date: serverPrice.Ending_Date,
                                Unit_Price: serverPrice.Unit_Price,
                                Currency_Code: serverPrice.Currency_Code,
                                Variant_Code: serverPrice.Variant_Code,
                                Source_Counter: serverPrice.Source_Counter,
                                lastSynced: new Date(),
                                pendingSync: false,
                                deleted: false
                            });
                        }
                    }

                    // Ejecutar operaciones en bloque
                    if (pricesToUpdate.length > 0) {
                        await db.transaction('rw', db.salesPrices, async () => {
                            for (const update of pricesToUpdate) {
                                await db.salesPrices.update(update.key, update.changes);
                            }
                        });
                    }

                    if (pricesToAdd.length > 0) {
                        await db.salesPrices.bulkAdd(pricesToAdd);
                    }

                    console.log("updatedPrices");

                    // Obtener todos los precios actualizados
                    const updatedPrices = await db.salesPrices
                        .filter((price) => !price.deleted)
                        .toArray();

                    console.log(`Obtenidos ${updatedPrices.length} precios de venta (${serverPrices.length} del servidor)`);
                    return updatedPrices;
                }
            }
        }

        console.log(`Sincronización de precios de venta completada: ${processedServerPrices} precios procesados`);
        return true;

    } catch (error) {
        console.error("Error al sincronizar precios de venta:", error);
        return false;
    }
};

// Función para obtener estadísticas de precios de venta locales
export const getSalesPricesStats = async () => {
    try {
        const allPrices = await db.salesPrices.toArray();
        const activePrices = allPrices.filter(p => !p.deleted);

        // Agrupar por tipo de venta
        const byType = {};
        activePrices.forEach(price => {
            const type = price.Sales_Type || "Sin tipo";
            byType[type] = (byType[type] || 0) + 1;
        });

        // Agrupar por moneda
        const byCurrency = {};
        activePrices.forEach(price => {
            const currency = price.Currency_Code || "Sin moneda";
            byCurrency[currency] = (byCurrency[currency] || 0) + 1;
        });

        return {
            total: allPrices.length,
            active: activePrices.length,
            byType,
            byCurrency,
            lastSync: allPrices.length > 0 ?
                Math.max(...allPrices.map(p => p.lastSynced?.getTime() || 0)) : null
        };
    } catch (error) {
        console.error("Error al obtener estadísticas de precios de venta:", error);
        return {
            total: 0,
            active: 0,
            byType: {},
            byCurrency: {},
            lastSync: null
        };
    }
};

// Función para limpiar precios de venta locales (útil para testing o reset)
export const clearLocalSalesPrices = async () => {
    try {
        await db.salesPrices.clear();

        console.log("Precios de venta locales limpiados correctamente");
        return true;
    } catch (error) {
        console.error("Error al limpiar precios de venta locales:", error);
        return false;
    }
};

// Función para forzar sincronización de precios de venta
export const forceSyncSalesPrices = async () => {
    console.log("Forzando sincronización de precios de venta...");
    return await syncSalesPrices();
}; 